import Alpine from 'alpinejs';

// Must stay in sync with the `lg:` breakpoint the navigation switches layout at.
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

/**
 * Picks the section a reader is currently looking at: the last heading that has already
 * passed the sticky header. Before the first heading no section is active, so the chapter
 * link alone stays highlighted.
 *
 * @param {Array<HTMLElement>} headings in document order
 * @param {number} offset distance from the viewport top that counts as "passed"
 * @returns {string|null} id of the active heading
 */
export function resolveActiveHeadingId(headings, offset) {
    let activeId = null;

    for (const heading of headings) {
        if (heading.getBoundingClientRect().top - offset > 1) {
            break;
        }
        activeId = heading.id || null;
    }

    return activeId;
}

Alpine.data('tutorialNavigation', () => ({
    open: false,
    isDesktop: false,

    get expanded() {
        return this.isDesktop || this.open;
    },

    init() {
        this._mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        this._onMediaChange = () => {
            this.isDesktop = this._mediaQuery.matches;
            if (this.isDesktop) {
                this.open = false;
            }
        };
        this._onMediaChange();
        this._mediaQuery.addEventListener('change', this._onMediaChange);

        this._sectionLinks = Array.from(this.$el.querySelectorAll('[data-tutorial-section-link]'));
        this._headings = Array.from(
            document.querySelectorAll('.tutorial-layout__content :is(h2, h3)[id]')
        );

        this._onScroll = () => {
            if (this._frame) {
                return;
            }
            this._frame = window.requestAnimationFrame(() => {
                this._frame = null;
                this.updateActiveSection();
            });
        };

        if (this._sectionLinks.length > 0 && this._headings.length > 0) {
            window.addEventListener('scroll', this._onScroll, { passive: true });
            window.addEventListener('resize', this._onScroll, { passive: true });
            this.updateActiveSection();
        }
    },

    destroy() {
        this._mediaQuery?.removeEventListener('change', this._onMediaChange);
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onScroll);
        if (this._frame) {
            window.cancelAnimationFrame(this._frame);
            this._frame = null;
        }
    },

    updateActiveSection() {
        // Matches where a jump mark lands (scroll-padding-top of `html` plus the heading's
        // scroll-margin-top), so clicking a section marks it active right away.
        const header = document.querySelector('.header');
        const offset = (header ? header.offsetHeight : 0) + 48;
        const activeId = resolveActiveHeadingId(this._headings, offset);

        for (const link of this._sectionLinks) {
            const isActive = activeId !== null && link.getAttribute('href') === `#${activeId}`;
            link.setAttribute('data-active', isActive ? 'true' : 'false');
            if (isActive) {
                link.setAttribute('aria-current', 'true');
            } else {
                link.removeAttribute('aria-current');
            }
        }
    },
}));
